import type { WritableDeep } from 'type-fest';

import { EMPTY_OBJECT } from '@e-socna/empty';
import { isDefined } from '@e-socna/utils';

import { getDeepSection } from '../../utils/get-deep-section.util';
import type { DocumentEditorCmp_RecursiveSection } from '../../fragments/recursive-section.fragment';
import type { Editable } from '../../types/editable.type';

import type { ReducerContext } from '../types/reducer-context.type';

export enum InsertPosition {
  Before = 'before',
  After = 'after',
  Inside = 'inside',
  Start = 'start',
  End = 'end',
}

interface InsertSectionParams<E extends Editable> {
  readonly clonedEditable: WritableDeep<E>;
  readonly path: readonly number[];
  readonly sections: readonly DocumentEditorCmp_RecursiveSection[];
  readonly position: InsertPosition;
  readonly context: ReducerContext<E>;
}

export const insertSections = <E extends Editable>({
  clonedEditable,
  path,
  sections,
  position,
  context,
}: Readonly<InsertSectionParams<E>>): void => {
  const getParentAndIndex = () => {
    if (position === InsertPosition.Inside || position === InsertPosition.End) {
      return {
        parent: getDeepSection(
          context.getEditable<true>(clonedEditable).virtualRoot,
          path,
        ),
        insertIndex: null,
      };
    }

    const parentPath = path.slice(0, -1);
    const [subSectionIndex] = path.slice(-1);

    if (!isDefined(subSectionIndex)) {
      throw new Error(`Invalid path for ${position} insertion`);
    }

    return {
      parent: getDeepSection(
        context.getEditable<true>(clonedEditable).virtualRoot,
        parentPath,
      ),
      insertIndex: subSectionIndex,
    };
  };

  const { parent, insertIndex } = getParentAndIndex();

  const inheritLayout = (
    section: WritableDeep<DocumentEditorCmp_RecursiveSection>,
  ) => ({
    ...section,
    ...(section.contentOriginId === context.contentOriginsCodeMap.Special._id
      ? { orientation: parent.orientation, paperFormat: parent.paperFormat }
      : EMPTY_OBJECT),
  });

  switch (position) {
    case InsertPosition.Before: {
      if (!isDefined(insertIndex)) {
        throw new Error('Insert index required for Before position');
      }

      sections.forEach((section, index) => {
        parent.subSections.splice(
          insertIndex + index,
          0,
          inheritLayout(
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          ),
        );
      });
      break;
    }

    case InsertPosition.After: {
      if (!isDefined(insertIndex)) {
        throw new Error('Insert index required for After position');
      }

      sections.forEach((section, index) => {
        parent.subSections.splice(
          insertIndex + index + 1,
          0,
          inheritLayout(
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          ),
        );
      });
      break;
    }

    case InsertPosition.Start: {
      sections.forEach((section, index) => {
        parent.subSections.splice(
          index,
          0,
          inheritLayout(
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          ),
        );
      });
      break;
    }

    case InsertPosition.Inside:
    case InsertPosition.End: {
      sections.forEach((section) => {
        parent.subSections.push(
          inheritLayout(
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          ),
        );
      });
      break;
    }

    default: {
      throw new Error(`Unknown insert position: ${position as string}`);
    }
  }
};
